import React, { useEffect, useState } from 'react';
import './Area.css';
import BasicCli from '../../../main/components/basicCli/BasicCli';
import EditModal from '../../../main/components/modal/edit/EditModal';
import eFormType from '../../../main/assets/enums/eFormType';
import DeleteModal from '../../../main/components/modal/delete/DeleteModal';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import IArea from '../../../main/interfaces/IArea';
import GeneralTable from '../../../main/components/grid/GeneralTable';
import IGridItems from '../../../main/interfaces/IGridItems';
import IPagination from '../../../main/interfaces/IPagination';
import { useForm } from 'react-hook-form';
import GeneralInput from '../../../main/components/generalInput/GeneralInput';
import axiosInstance from '../../../main/initializers/axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const Area = () => {
  const { i18n } = useTranslation();

  const [areas, setAreas] = useState<IArea[]>([]);
  const [isModalAddOpen, setIsModalAddOpen] = useState(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState(false);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState<IArea | null>(null);
  const [approved, setApproved] = useState(false);

  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IArea>({
    defaultValues: { code: '', name: '' },
  });

  const fetchAreas = (pgOverride?: IPagination, gridOverride?: IGridItems[]) => {
    const pg = pgOverride ?? pagination;
    const effectiveGrid = gridOverride ?? gridItems ?? [];

    axiosInstance
      .post(ApiConstants.area.listct, {
        gridItems: effectiveGrid,
        pagination: pg,
      })
      .then((res) => {
        setAreas(res.data.items ?? []);
        setGridItems(res.data.gridItems ?? []);
        setPagination({
          ...pg,
          count: res.data.pagination?.count ?? pg.count,
        });
      })
      .catch(() => {
        toast.error(i18n.t('errors.fetchAreas'));
      });
  };

  useEffect(() => {
    fetchAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePaginationChange = (item: IPagination) => {
    setPagination(item);
    fetchAreas(item);
  };

  const openAddModal = () => {
    reset({ code: '', name: '' });
    setApproved(false);
    setIsModalAddOpen(true);
  };

  const openEditModal = (area: IArea) => {
    setSelectedArea(area);
    reset({ code: area.code, name: area.name });
    setApproved(false);
    setIsModalEditOpen(true);
  };

  const openDeleteModal = (area: IArea) => {
    setSelectedArea(area);
    setIsModalDeleteOpen(true);
  };

  const handleAddOrUpdate = (data: IArea) => {
    const params = {
      id: selectedArea?.id ?? 0,
      name: data.name,
      code: data.code,
    };

    axiosInstance
      .post(ApiConstants.area.update, params)
      .then((response) => {
        if (response.status === 200) {
          toast.success(
            selectedArea
              ? i18n.t('area.areaUpdatedSuccessfully')
              : i18n.t('area.areaCreatedSuccessfully'),
          );
          setIsModalAddOpen(false);
          setIsModalEditOpen(false);
          setSelectedArea(null);
          fetchAreas();
        } else {
          toast.error(i18n.t('errors.somethingWentWrong'));
        }
      })
      .catch(() => {
        toast.error(i18n.t('errors.somethingWentWrong'));
      });
  };

  const handleDelete = () => {
    if (!selectedArea?.id) return;

    axiosInstance
      .post(ApiConstants.area.delete, { id: selectedArea.id })
      .then((response) => {
        if (response.status === 200) {
          toast.success(i18n.t('area.areaDeletedSuccessfully'));
          setIsModalDeleteOpen(false);
          setSelectedArea(null);
          setAreas((prev) => prev.filter((a) => a.id !== selectedArea.id));
        } else {
          toast.error(i18n.t('errors.somethingWentWrong'));
        }
      })
      .catch(() => {
        toast.error(i18n.t('errors.areaUsedCannotDelete'));
      });
  };

  return (
    <BasicCli>
      <div className="areaPage">
        <div className="areaContainer">
          <div className="ui-panel ui-toolbar areaToolbar">
            <div className="areaTitleBlock">
              <h2 className="areaTitle">{i18n.t('area.manageAreas')}</h2>
            </div>
            <div className="areaActions">
              <button type="button" className="ui-control ui-btn ui-btn--primary" onClick={openAddModal}>
                {i18n.t('area.addArea')}
              </button>
            </div>
          </div>

          <GeneralTable
            data={areas}
            columns={gridItems}
            gridEntity={21}
            title=""
            hasRp
            selectedItem={(val) => setSelectedArea(val)}
            onEdit={openEditModal}
            onDelete={openDeleteModal}
            pagination={pagination}
            selectedItemPagination={(val: any) => handlePaginationChange(val)}
            onFilterClose={(nextGridItems) => {
              setGridItems(nextGridItems);
              fetchAreas(undefined, nextGridItems);
            }}
          />
        </div>

        {isModalAddOpen && (
          <EditModal
            title={i18n.t('area.addArea')}
            width="500px"
            closeModal={() => setIsModalAddOpen(false)}
            onSubmitData={() => setApproved(true)}
            setIsModalOpen={setIsModalAddOpen}
            modalType={eFormType.New}
          >
            <AreaForm
              control={control}
              errors={errors}
              approved={approved}
              onSubmit={() => {
                handleSubmit(handleAddOrUpdate)();
              }}
            />
          </EditModal>
        )}

        {isModalEditOpen && (
          <EditModal
            title={i18n.t('area.editArea')}
            width="500px"
            closeModal={() => setIsModalEditOpen(false)}
            onSubmitData={() => setApproved(true)}
            setIsModalOpen={setIsModalEditOpen}
            modalType={eFormType.Edit}
          >
            <AreaForm
              control={control}
              errors={errors}
              approved={approved}
              onSubmit={() => {
                handleSubmit(handleAddOrUpdate)();
              }}
            />
          </EditModal>
        )}

        {isModalDeleteOpen && selectedArea && (
          <DeleteModal
            width="500px"
            handleClose={() => setIsModalDeleteOpen(false)}
            handleDelete={handleDelete}
          >
            {i18n.t('area.deleteConfirm', { name: selectedArea.name })}
            <br />
            <small className="text-muted">{i18n.t('area.deleteCannotUndo')}</small>
          </DeleteModal>
        )}
      </div>
    </BasicCli>
  );
};

// Internal Area form component
const AreaForm: React.FC<{
  control: any;
  errors: any;
  approved: boolean;
  onSubmit: () => void;
}> = ({ control, errors, approved, onSubmit }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (approved) {
      onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approved]);

  return (
    <div className="areaForm">
      <div className="areaField">
        <GeneralInput
          required
          label={i18n.t('area.code')}
          name="code"
          type="text"
          placeholder={i18n.t('area.code')}
          errors={errors}
          control={control}
        />
      </div>
      <div className="areaField">
        <GeneralInput
          required
          label={i18n.t('area.name')}
          name="name"
          type="text"
          placeholder={i18n.t('area.name')}
          errors={errors}
          control={control}
        />
      </div>
    </div>
  );
};

export default Area;
